/**
 * The MapLibre style the viewer draws, derived from the objects a token grants.
 *
 * Pure on purpose: `MapView` only diffs what comes out of here against what the map already
 * holds, and the tests can check the whole style without a map.
 */
import { CHART, CROWN_HEALTH, MAP, MATURITY, POSITION_SOURCE, SPECIES_COLORS, STRESS_STOPS, viridis } from '@/theme/colors';
import { POSITION_SOURCES, type PhotoPositioningSummary } from './photoPositioning';
import { spectralQuery, type SpectralView } from './spectral';

// `||`, not `??`: the production image is built with these args unset, which arrive as ''.

/**
 * Raster basemap. OpenStreetMap's own tiles are fine for development only — production needs
 * a tile-provider decision (usage policy, attribution, key), hence the env override.
 */
export const BASEMAP_URL: string = import.meta.env.VITE_BASEMAP_URL || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
export const BASEMAP_ATTRIBUTION = '© OpenStreetMap contributors';
/**
 * Aerial basemap. The Czech state orthophoto (ČÚZK, open data) in Web Mercator; it covers the
 * country the forests are in and needs no key. ArcGIS tile order is {z}/{y}/{x}.
 */
export const ORTHO_BASEMAP_URL: string =
  import.meta.env.VITE_ORTHO_BASEMAP_URL || 'https://ags.cuzk.gov.cz/arcgis1/rest/services/ORTOFOTO_WM/MapServer/tile/{z}/{y}/{x}';
export const ORTHO_BASEMAP_ATTRIBUTION = '© ČÚZK';

export type Basemap = 'osm' | 'ortho' | 'none';
export const BASEMAPS: Record<Exclude<Basemap, 'none'>, { url: string; attribution: string }> = {
  osm: { url: BASEMAP_URL, attribution: BASEMAP_ATTRIBUTION },
  ortho: { url: ORTHO_BASEMAP_URL, attribution: ORTHO_BASEMAP_ATTRIBUTION },
};
/** Glyphs for the cluster count labels. Same caveat as the basemap: production needs its own. */
export const GLYPHS_URL: string = import.meta.env.VITE_GLYPHS_URL || 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf';
export const LABEL_FONT = ['Noto Sans Regular'];

/** How the browser reaches an object: raster tiles from the tiler, a signed GeoPackage link, or not at all. */
export type Via = 'tiler' | 'link' | 'none';

/** Forint's data-object kinds, which decide how a layer is styled. */
export type ObjectKind =
  | 'orthophoto'
  | 'dsm'
  | 'dtm'
  | 'chm'
  | 'ortho_tree_analysis'
  | 'lhp'
  | 'boundary'
  | 'drone_photos_raw'
  | 'lidar_mls'
  | 'forest_summary'
  | 'mls_tree_analysis'
  | 'chm_quality'
  | 'field_annotations'
  | 'multispectral_orthophoto'
  | 'crown_health';

/** One object a viewer token grants, with the object kind the caller already knows. */
export interface LayerRef {
  object: string;
  object_kind: ObjectKind;
  via: Via;
  /** The file inside the object's folder: the GeoPackage for a `link` object. */
  path: string;
  /** Changes whenever the object's file is rewritten; keys the tile and meta URLs. */
  digest: string;
  /**
   * One feature table of the object's GeoPackage, drawn as a layer of its own; `object` is then
   * `<object id>:<table>` so it has its own source and its own row in the layer panel.
   */
  table?: string;
  /**
   * Vector tiles that already hold this layer (a PMTiles archive): its URL template, the source
   * it shares with the object's other layers, its source layers and the feature id per layer.
   */
  tiles?: TiledSource;
}

export interface TreeRules {
  /** Target DBH (cm) by palette genus; a species genus not listed is mature from `fallback`. */
  minDbh: Record<string, number>;
  fallback: number;
  /** Palette keys that are never mature (Unknown, Snag, Shrub). */
  nonSpecies: string[];
  /** The height layers' cuts (m), or null without heights. */
  strata: { lo: number; hi: number } | null;
}

export interface TiledSource {
  url: string;
  /** Key of the one MapLibre source the layers of an archive share. */
  source: string;
  maxzoom: number;
  layers: { points: string; polygons: string };
  /** Which property is each source layer's feature id (feature state needs one; 0 is an id too). */
  promoteId?: Record<string, string>;
  /** A third source layer of polygons, a crown health screen's hotspots. */
  areas?: string;
}

/** An acquisition whose photo points the map draws from the bucket, keyed by its object id. */
export interface PhotoObjectRef {
  object: string;
  object_kind: 'drone_photos_raw';
  /** The tilt from nadir (degrees) above which the photogrammetry leaves a photo out, as its preprocess run applied it. */
  maxTiltDeg?: number | null;
  /** Where its camera positions come from, as its preprocess run decided them; `null` for a run before that. */
  positioning?: PhotoPositioningSummary | null;
}

/** The photogrammetry's tilt limit when the preprocess run did not say which it applied (forestum `max_tilt_deg`). */
export const DEFAULT_MAX_TILT_DEG = 10;

/** The tilt limit of an acquisition: its preprocess run's, else the default. */
export const tiltLimitOf = (ref: PhotoObjectRef): number => ref.maxTiltDeg ?? DEFAULT_MAX_TILT_DEG;

/** A photo point the photogrammetry leaves out for its tilt, while the points are coloured by tilt: ringed red. */
export const PHOTO_TILT_EXCLUDED_LOOK = { fill: null, stroke: MAP.photoExcluded, strokeWidth: 2.5 } as const satisfies PointLook;

export type ColorBy = 'species' | 'height' | 'dbh' | 'volume' | 'maturity' | 'health' | 'stress';
export type Stratum = 'upper' | 'middle' | 'lower';
export type NumericColorBy = Exclude<ColorBy, 'species' | 'maturity' | 'health' | 'stress'>;

export interface LayerSettings {
  visible?: boolean;
  opacity?: number;
}

/** A filter range; `null` leaves that side open, so a slider's end does not cut off what lies beyond it. */
export type FilterRange = [number | null, number | null];

export interface LayerFilters {
  /** Genera to keep; empty or absent keeps everything. */
  species?: string[];
  height?: FilterRange;
  /** DBH range in cm; trees without a DBH are dropped. */
  dbh?: FilterRange;
  /** Height layers to keep; empty or absent keeps all. */
  strata?: Stratum[];
  /** Keep only trees at or above their harvest-maturity DBH. */
  matureOnly?: boolean;
  /** Keep only the crowns a health screen found dead, snag or declining. */
  flaggedOnly?: boolean;
}

export type PhotoColorBy = 'none' | 'agl_m' | 'forward_overlap_pct' | 'side_overlap_pct' | 'tilt_deg' | 'geo_error_h_m' | 'position_source';
/** The photo colourings on a ramp; `position_source` is a category per photo. */
export type PhotoRampBy = Exclude<PhotoColorBy, 'none' | 'position_source'>;

/** Whether some acquisition says where its camera positions come from. */
export const hasPositioning = (photos: PhotoObjectRef[]): boolean => photos.some((p) => !!p.positioning);

/** What the photo points are coloured by: the page's choice, else by position source when an acquisition says it, else one colour. */
export function photoColorByOf(options: LayerOptions, photos: PhotoObjectRef[]): PhotoColorBy {
  const by = options.photoColorBy ?? (hasPositioning(photos) ? 'position_source' : 'none');
  return by === 'position_source' && !hasPositioning(photos) ? 'none' : by;
}

export type CrownStyle = 'fill' | 'outline';
export const CROWN_STYLES: CrownStyle[] = ['fill', 'outline'];

export interface LayerOptions {
  /** The tiler's origin, from the viewer token. */
  baseUrl: string;
  basemap?: Basemap;
  /** Object ids top-first, as the layer panel lists them; objects not named keep their given order, below. */
  layers?: string[];
  settings?: Record<string, LayerSettings>;
  colorBy?: ColorBy;
  /** What the photo points are coloured by; independent of the trees' `colorBy`. */
  photoColorBy?: PhotoColorBy;
  ranges?: Partial<Record<NumericColorBy, [number, number]>>;
  filters?: LayerFilters;
  /** Mature crowns get a heavy outline. */
  highlightMature?: boolean;
  /** Crowns as coloured areas (the default) or as coloured outlines over a see-through inside. */
  crownStyle?: CrownStyle;
  /** Genera actually present in the data; the species filter offers only these. */
  species?: string[];
  /** Bumped when a crown layer's tiles must be fetched again (its maturity tags changed). */
  crownVersion?: number;
  /** The report's map frame spans the viewport instead of the page column. */
  wide?: boolean;
  /** The scan area the coverage report has selected (its `area_id`); not stored with the view. */
  selectedArea?: number;
  /**
   * How a crown is mature and in which height layer when its tiles carry no tags of the page's
   * (a tile archive): the rules themselves, evaluated per feature; not stored with the view.
   */
  treeRules?: TreeRules;
  /** The units the growing-stock report highlights (their `id`); not stored with the view. */
  selectedUnits?: string[];
  /** What a multispectral orthophoto shows (an index, false colour, a band); false colour without it. Not stored with the view. */
  spectral?: SpectralView;
  /** How many crowns a health screen put in each category, for the legend; not stored with the view. */
  healthCounts?: Partial<Record<HealthCategory, number>>;
}

export interface TileSourceSpec {
  type: 'raster';
  tiles: string[];
  tileSize?: number;
  minzoom?: number;
  maxzoom?: number;
  attribution?: string;
}
export interface GeoJsonSourceSpec {
  type: 'geojson';
  data: { type: 'FeatureCollection'; features: never[] };
  cluster?: boolean;
  clusterMaxZoom?: number;
  clusterRadius?: number;
}
/** Crown layers: tiles cut by the layer's own worker, served through the `gpkg://` protocol. */
export interface VectorSourceSpec {
  type: 'vector';
  tiles: string[];
  minzoom?: number;
  maxzoom?: number;
  promoteId?: Record<string, string>;
}
export type SourceSpec = TileSourceSpec | GeoJsonSourceSpec | VectorSourceSpec;

export interface LayerSpec {
  id: string;
  type: 'raster' | 'circle' | 'fill' | 'line' | 'symbol';
  source: string;
  'source-layer'?: string;
  minzoom?: number;
  maxzoom?: number;
  filter?: unknown[];
  layout?: Record<string, unknown>;
  paint?: Record<string, unknown>;
  /** `area`: the layer draws areas (hotspots, units, scan areas, boundaries), which a click picks only where it hits no tree (`pickHit`). */
  metadata?: { area?: boolean };
}

export interface BuiltStyle {
  sources: Record<string, SourceSpec>;
  layers: LayerSpec[];
}

export const BASEMAP_SOURCE = 'src-basemap';
export const BASEMAP_LAYER = 'lyr-basemap';

/** The lowest CHM height drawn and the floor of its ramp; a photogrammetric CHM dips below ground. */
const CHM_FLOOR_M = -5;

/** How the tiler renders each elevation model (docs/tiler-api.md §2.2). */
const DEM_RENDERING: Partial<Record<ObjectKind, string>> = {
  dsm: '&hillshade=1',
  dtm: '&contours=1',
  // A CHM without declared nodata fills its gaps with -9999.
  chm: `&colormap=viridis&min_valid=${CHM_FLOOR_M}`,
};

/** The heights the tiler's CHM ramp spans: its default stretch is the file's range, floored at CHM_FLOOR_M by `min_valid`. */
export function chmRampRange([low, high]: [number, number]): [number, number] {
  return [Math.max(CHM_FLOOR_M, low), Math.max(CHM_FLOOR_M, high)];
}

/** The CHM legend: the part of the ramp from 0 m up, as the heights below ground are not canopy. */
export function chmLegend(range: [number, number]): { low: number; high: number; colors: string[] } {
  const [low, high] = chmRampRange(range);
  const shown = high > 0 ? Math.max(0, low) : low;
  return { low: shown, high, colors: rampColors(high > low ? (shown - low) / (high - low) : 0) };
}

/** Crowns are drawn as centroids below this zoom, where a crown is a pixel, and as outlines from it up. */
export const CROWN_POLYGON_MIN_ZOOM = 16;
export const CROWN_TILE_MAX_ZOOM = 20;
/** Layer names inside a crown tile (crownLayer.ts), as `source-layer` refers to them. */
export const CROWN_LAYERS = { points: 'trees', polygons: 'crowns' } as const;

/** Above this zoom the points are drawn one by one; below it MapLibre clusters them. */
export const PHOTO_CLUSTER_MAX_ZOOM = 14;
export const PHOTO_CLUSTER_RADIUS = 40;

/** forun's crown attributes (forun-api.md §5.5) and how the viewer shows them. */
export const TREE_FIELDS = {
  genus: 'genus',
  genusCzech: 'genus_czech',
  height: { prop: 'height', scale: 1, unitKey: 'units.m' },
  dbh: { prop: 'DBH', scale: 100, unitKey: 'units.cm' },
  /** The visible volume (the direct volume model's); the volume colouring reads it. */
  volume: { prop: 'volume', scale: 1, unitKey: 'units.m3' },
  /** The volume hidden below the crown, which the visible volume leaves out. */
  underVolume: { prop: 'under_volume', scale: 1, unitKey: 'units.m3' },
} as const;

/** What a field crew's trunk carries (the `trunk` table of `field_annotations.gpkg`, forun-api.md §5.6). */
export const FIELD_FIELDS = {
  id: 'trunk_id',
  dbh: { prop: 'dbh_mm', scale: 0.1, unitKey: 'units.cm' },
  height: { prop: 'height_m', scale: 1, unitKey: 'units.m' },
  heightSource: 'height_source',
  status: 'status',
  note: 'note',
  flags: ['snag', 'stump', 'forked'],
} as const;

/** The tables of `field_annotations.gpkg`: one point per trunk, the surveyed areas around them. */
export const FIELD_TABLES = { trunk: 'trunk', area: 'area' } as const;

/** What an MLS segment carries besides: its own volume estimate and how sure its classification is. */
export const MLS_FIELDS = {
  volume: { prop: 'allometric_volume', scale: 1, unitKey: 'units.m3' },
  confidence: { prop: 'species_confidence', scale: 100, unitKey: 'units.pct' },
  invalid: { prop: 'p_invalid', scale: 100, unitKey: 'units.pct' },
} as const;

/**
 * What a crown health screen writes on each crown and hotspot (forestum `crown_health`, its
 * `crown_health.pmtiles`): the category, the stress z-score and the crown top's indices.
 */
export const HEALTH_FIELDS = {
  category: 'category',
  stress: 'stress_z',
  reference: 'reference',
  distance: { prop: 'distance_to_source_m', scale: 1, unitKey: 'units.m' },
  ndvi: 'ndvi_median',
  ndre: 'ndre_median',
  hotspot: 'hotspot',
} as const;

/** The categories, worst first: screened ones, then what the screen could not judge. */
export const HEALTH_CATEGORIES = ['dead', 'snag', 'declining', 'threatened', 'healthy', 'no_reference', 'no_data', 'outside', 'skipped'] as const;
export type HealthCategory = (typeof HEALTH_CATEGORIES)[number];
/** The categories the "only dead, snags and declining" switch keeps, and a hotspot gathers. */
export const HEALTH_FLAGGED: HealthCategory[] = ['dead', 'snag', 'declining'];
/** The stress z-score's ramp: −6 (far worse than the neighbours) to +2. */
export const STRESS_RANGE: [number, number] = [STRESS_STOPS[0]!.value, STRESS_STOPS[STRESS_STOPS.length - 1]!.value];
/** The colourings a crown health screen's crowns offer: no DBH, and no height or volume at the points. */
export const HEALTH_COLOR_BY: ColorBy[] = ['health', 'stress', 'species'];
/** The table a crown health ref names to draw the hotspots rather than the crowns. */
export const HEALTH_HOTSPOTS = 'hotspots';

/** A crown's category colour; a crown without one in the no-value grey. */
export function healthColorExpression(): unknown[] {
  return ['match', ['get', HEALTH_FIELDS.category], ...HEALTH_CATEGORIES.flatMap((c) => [c, CROWN_HEALTH[c]]), MAP.treeNoValue];
}

/** The stress z-score over its diverging ramp; a crown without one (not screened) in the no-value grey. */
export function stressColorExpression(): unknown[] {
  const stops = STRESS_STOPS.flatMap(({ value, color }) => [value, color]);
  return ['case', ['==', ['typeof', ['get', HEALTH_FIELDS.stress]], 'number'], ['interpolate', ['linear'], ['get', HEALTH_FIELDS.stress], ...stops], MAP.treeNoValue];
}

/** Properties the trees report computes and adds to each crown before the map draws it. */
export const TREE_EXTRAS = { mature: '_mature', stratum: '_stratum' } as const;

/** The tree layers' attribute tables: photo crowns, a mobile scan's stems and crowns, a field crew's trunks. */
export type TreeSchema = 'photo' | 'mls' | 'field';

/** A tree attribute and the factor that brings it to the unit its colour ramp is in. */
export interface Measure {
  prop: string;
  scale: number;
}

/**
 * What each tree layer calls a colouring quantity, scaled to one unit for all of them (m, cm, m³),
 * so one ramp colours them alike. Null: the layer does not carry it (a field trunk has no volume).
 */
export const TREE_MEASURES: Record<TreeSchema, Record<NumericColorBy, Measure | null>> = {
  photo: { height: TREE_FIELDS.height, dbh: TREE_FIELDS.dbh, volume: TREE_FIELDS.volume },
  // An MLS segment's volume is its allometric estimate; it has no `volume` column.
  mls: { height: TREE_FIELDS.height, dbh: TREE_FIELDS.dbh, volume: MLS_FIELDS.volume },
  field: { height: FIELD_FIELDS.height, dbh: FIELD_FIELDS.dbh, volume: null },
};

/** The unit each colouring quantity's ramp is in, whichever layer it colours. */
export const COLOR_UNITS: Record<NumericColorBy, string> = { height: 'units.m', dbh: 'units.cm', volume: 'units.m3' };

/** A measure as a MapLibre expression in its ramp's unit. */
export function measureExpression(m: Measure): unknown[] {
  return ['*', ['get', m.prop], m.scale];
}

/** Whether a feature carries the measure as a number. */
export function hasMeasure(m: Measure): unknown[] {
  return ['==', ['typeof', ['get', m.prop]], 'number'];
}

export const DEFAULT_RANGES: Record<NumericColorBy, [number, number]> = {
  height: [0, 45],
  dbh: [0, 80],
  volume: [0, 5],
};

/** Colour stops on the viridis ramp; six is enough to read and short enough to render. */
export const RAMP_STOPS = 6;

export const sourceId = (object: string) => `src-${object}`;
/** The tile url template of a crown object; the digest and version make MapLibre refetch when either changes. */
export const crownTileUrl = (object: string, digest: string, version = 0) => `gpkg://${object}/{z}/{x}/{y}?d=${encodeURIComponent(digest)}&v=${version}`;
export const isCrownKind = (kind: ObjectKind) => kind === 'ortho_tree_analysis';
export const layerId = (object: string, role: string) => `lyr-${object}-${role}`;

/**
 * The palette key of a genus value: its first word, first letter upper-cased. Forun writes
 * Latin genera capitalised and its special classes (`unknown`, `shrub`, `snag`) in lower case.
 */
export function genusOf(value: unknown): string {
  const text = typeof value === 'string' ? value.trim() : '';
  const space = text.indexOf(' ');
  const word = space > 0 ? text.slice(0, space) : text;
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** `genusOf` as a MapLibre expression over the genus property; a binomial still works. */
export function genusExpression(): unknown[] {
  const genus = ['get', TREE_FIELDS.genus];
  const space = ['index-of', ' ', genus];
  const word = ['case', ['>', space, 0], ['slice', genus, 0, space], genus];
  return ['concat', ['upcase', ['slice', word, 0, 1]], ['slice', word, 1]];
}

/** The genera a page's species filter keeps as a MapLibre filter; none (all kept) for an empty choice. */
export function genusFilter(genera: string[] | undefined): unknown[] | undefined {
  return genera?.length ? ['in', genusExpression(), ['literal', genera]] : undefined;
}

function speciesColorExpression(): unknown[] {
  const pairs = Object.entries(SPECIES_COLORS).flatMap(([genus, color]) => [genus, color]);
  return ['match', genusExpression(), ...pairs, SPECIES_COLORS.Unknown];
}

/** The ramp's colours, or its upper part from `from` (0–1) on. */
export function rampColors(from = 0): string[] {
  return Array.from({ length: RAMP_STOPS }, (_, i) => viridis(from + ((1 - from) * i) / (RAMP_STOPS - 1)));
}

/** The ramp's stops over a range: the values the colours sit at, which the legend labels too. */
export function rampStops([min, max]: readonly [number, number]): { value: number; color: string }[] {
  return rampColors().map((color, i) => ({ value: min + ((max - min) * i) / (RAMP_STOPS - 1), color }));
}

/** A tree layer's ramp over the shared range, from its own attribute; a tree without it in the no-value grey. */
export function rampExpression(schema: TreeSchema, by: NumericColorBy, range: [number, number]): unknown {
  const m = TREE_MEASURES[schema][by];
  if (!m) return MAP.treeNoValue;
  const stops = rampStops(range).flatMap(({ value, color }) => [value, color]);
  return ['case', hasMeasure(m), ['interpolate', ['linear'], measureExpression(m), ...stops], MAP.treeNoValue];
}

/** Fixed ramps for the photo points: comparable between acquisitions, values beyond clamp to the ends. */
export const PHOTO_RANGES = {
  agl_m: { range: [0, 150], unitKey: 'units.m' },
  forward_overlap_pct: { range: [50, 100], unitKey: 'units.pct' },
  side_overlap_pct: { range: [50, 100], unitKey: 'units.pct' },
  tilt_deg: { range: [0, 30], unitKey: 'units.deg' },
  geo_error_h_m: { range: [0, 5], unitKey: 'units.m' },
} as const satisfies Record<PhotoRampBy, { range: [number, number]; unitKey: string }>;

function photoColor(by: PhotoColorBy): unknown {
  if (by === 'none') return MAP.photoPoint;
  if (by === 'position_source') return ['match', ['get', 'position_source'], ...POSITION_SOURCES.flatMap((s) => [s, POSITION_SOURCE[s]]), CHART.dotMuted];
  const stops = rampStops(PHOTO_RANGES[by].range).flatMap(({ value, color }) => [value, color]);
  return ['case', ['==', ['typeof', ['get', by]], 'number'], ['interpolate', ['linear'], ['get', by], ...stops], CHART.dotMuted];
}

/** The one range a quantity's ramp spans on every tree layer. */
export function rangeOf(options: LayerOptions, by: NumericColorBy): [number, number] {
  return options.ranges?.[by] ?? DEFAULT_RANGES[by];
}

/** Whether a crown is mature: the page's tag, or the rules evaluated on the feature. */
function matureExpression(options: LayerOptions, dbh: Measure = TREE_FIELDS.dbh): unknown[] {
  const r = options.treeRules;
  if (!r) return ['==', ['get', TREE_EXTRAS.mature], true];
  const genus = genusExpression();
  const pairs = Object.entries(r.minDbh).flatMap(([g, min]) => [g, min]);
  const target = pairs.length ? ['match', genus, ...pairs, r.fallback] : r.fallback;
  return ['all', ['!', ['in', genus, ['literal', r.nonSpecies]]], ['has', dbh.prop], ['>=', ['*', ['to-number', ['get', dbh.prop], -1], dbh.scale], target]];
}

/** A crown's height layer: the page's tag, or the cuts applied to its height. */
function stratumExpression(options: LayerOptions): unknown {
  const r = options.treeRules;
  if (!r) return ['get', TREE_EXTRAS.stratum];
  if (!r.strata) return '';
  const h = ['to-number', ['get', TREE_FIELDS.height.prop], -1];
  return ['case', ['!', ['has', TREE_FIELDS.height.prop]], '', ['<=', h, r.strata.lo], 'lower', ['<=', h, r.strata.hi], 'middle', 'upper'];
}

/**
 * Harvest maturity by a layer's own DBH under the page's rules. Without rules only the photo
 * crowns know it (the page tags them), so the other layers are drawn without a value.
 */
function maturityColor(options: LayerOptions, schema: TreeSchema): unknown {
  const r = options.treeRules;
  if (!r) return schema === 'photo' ? ['case', matureExpression(options), MATURITY.mature, MATURITY.immature] : MAP.treeNoValue;
  const dbh = TREE_MEASURES[schema].dbh!;
  return ['case', ['!', hasMeasure(dbh)], MAP.treeNoValue, matureExpression(options, dbh), MATURITY.mature, MATURITY.immature];
}

/** A tree layer's colour under the panel's one choice: the same genus, quantity or maturity colours on every tree layer. */
export function treeColor(options: LayerOptions, schema: TreeSchema): unknown {
  const by = options.colorBy ?? 'species';
  if (by === 'species') return speciesColorExpression();
  if (by === 'maturity') return maturityColor(options, schema);
  if (by === 'health') return healthColorExpression();
  if (by === 'stress') return stressColorExpression();
  return rampExpression(schema, by, rangeOf(options, by));
}

function rangeClauses(value: unknown[], range: FilterRange | undefined): unknown[][] {
  const [lo, hi] = range ?? [null, null];
  return [...(lo === null ? [] : [['>=', value, lo]]), ...(hi === null ? [] : [['<=', value, hi]])];
}

/** Whether `value` lies in `range`; an absent value passes only a range open on both sides. */
export function inRange(value: number | null, range: FilterRange | undefined): boolean {
  const [lo, hi] = range ?? [null, null];
  if (lo === null && hi === null) return true;
  return value !== null && (lo === null || value >= lo) && (hi === null || value <= hi);
}

/** The tree filters but the genus one: what an MLS segment that is no tree is spared. */
function measureClauses(filters: LayerFilters | undefined, options?: LayerOptions): unknown[][] {
  const clauses: unknown[][] = [];
  clauses.push(...rangeClauses(['get', TREE_FIELDS.height.prop], filters?.height));
  clauses.push(...rangeClauses(['*', ['to-number', ['get', TREE_FIELDS.dbh.prop], -1], TREE_FIELDS.dbh.scale], filters?.dbh));
  if (filters?.strata?.length) clauses.push(['in', stratumExpression(options ?? { baseUrl: '' }), ['literal', filters.strata]]);
  if (filters?.matureOnly) clauses.push(matureExpression(options ?? { baseUrl: '' }));
  if (filters?.flaggedOnly) clauses.push(['in', ['get', HEALTH_FIELDS.category], ['literal', HEALTH_FLAGGED]]);
  return clauses;
}

const allOf = (clauses: unknown[][]): unknown[] | undefined => (clauses.length ? ['all', ...clauses] : undefined);

function treeFilter(filters: LayerFilters | undefined, options?: LayerOptions): unknown[] | undefined {
  const genus = genusFilter(filters?.species);
  return allOf([...(genus ? [genus] : []), ...measureClauses(filters, options)]);
}

/**
 * An MLS segment's filter: the genus choice hides a segment that is no tree too (it has no genus
 * to keep), the measures spare it (it is drawn grey for what it is).
 */
function mlsFilter(filters: LayerFilters | undefined): unknown[] | undefined {
  const genus = genusFilter(filters?.species);
  const measures = allOf(measureClauses(filters));
  return allOf([...(genus ? [genus] : []), ...(measures ? [['any', ['==', ['get', TREE_FIELDS.genus], MLS_INVALID], measures]] : [])]);
}

/** Elevation models cover everything below them, so they start faint. */
const DEM_OPACITY = 0.3;

export function opacityOf(options: LayerOptions, object: string, kind: ObjectKind): number {
  return options.settings?.[object]?.opacity ?? (kind in DEM_RENDERING ? DEM_OPACITY : 1);
}

/** Whether a layer is on; elevation models start off because they cover everything below them. */
export function isVisible(options: LayerOptions, object: string, kind: ObjectKind): boolean {
  return options.settings?.[object]?.visible ?? !(kind in DEM_RENDERING);
}
const visibilityOf = (options: LayerOptions, object: string, kind: ObjectKind) =>
  isVisible(options, object, kind) ? 'visible' : 'none';

function rasterTiles(options: LayerOptions, ref: LayerRef): string {
  return rasterTileTemplate(options.baseUrl, ref, options.spectral);
}

/**
 * Pixels per side of a tiler tile. A 512 px tile covers its z/x/y footprint at twice the density,
 * so it stands in for the four 256 px tiles of zoom z + 1, and the tiler's fixed cost per request
 * (opening the file, building the transformer) is paid once instead of four times: a screen
 * renders 2–3.5× faster in 4× fewer requests (docs/tiler-api.md, `size`). MapLibre then asks for
 * one zoom level less for the same detail; the source keeps MapLibre's default maxzoom of 22, the
 * map's own maximum, so the deepest tiles carry 256 px z23 detail, and the tiler upsamples above
 * a raster's native resolution anyway.
 */
export const RASTER_TILE_SIZE = 512;

/** The zoom of the tiler tiles MapLibre draws at map zoom `zoom` (raster sources round). */
export function rasterTileZoom(zoom: number): number {
  return Math.round(zoom + Math.log2(512 / RASTER_TILE_SIZE));
}

/** The tile URL template of a tiler object, with the kind's rendering parameters. */
export function rasterTileTemplate(baseUrl: string, ref: LayerRef, spectral?: SpectralView): string {
  const url = `${baseUrl}/v1/objects/${ref.object}/tiles/{z}/{x}/{y}.webp?d=${encodeURIComponent(ref.digest)}&size=${RASTER_TILE_SIZE}`;
  if (ref.object_kind === 'multispectral_orthophoto') return url + spectralQuery(spectral);
  return url + (DEM_RENDERING[ref.object_kind] ?? '');
}

/** One tile's URL, exactly as MapLibre asks for it, so the browser cache answers a second read. */
export function rasterTileUrl(baseUrl: string, ref: LayerRef, z: number, x: number, y: number): string {
  return rasterTileTemplate(baseUrl, ref).replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
}

function orderRefs(refs: LayerRef[], order: string[] | undefined): LayerRef[] {
  if (!order?.length) return refs;
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...refs]
    .map((r, i) => ({ r, key: rank.get(r.object) ?? order.length + i }))
    .sort((l, r) => l.key - r.key)
    .map(({ r }) => r);
}

/** The refs of the listed kinds, in that top-first order; equal kinds keep their given order. */
export function byKind(refs: LayerRef[], topFirst: ObjectKind[]): LayerRef[] {
  return refs
    .filter((r) => topFirst.includes(r.object_kind))
    .map((r, i) => ({ r, i, rank: topFirst.indexOf(r.object_kind) }))
    .sort((l, r) => l.rank - r.rank || l.i - r.i)
    .map(({ r }) => r);
}

function rasterOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  built.sources[sourceId(ref.object)] = { type: 'raster', tiles: [rasterTiles(options, ref)], tileSize: RASTER_TILE_SIZE };
  built.layers.push({
    id: layerId(ref.object, 'raster'),
    type: 'raster',
    source: sourceId(ref.object),
    layout: { visibility: visibilityOf(options, ref.object, ref.object_kind) },
    paint: { 'raster-opacity': opacityOf(options, ref.object, ref.object_kind) },
  });
}

/** Starts empty; the map fills it once the object's GeoPackage has been read. */
function vectorSource(ref: LayerRef, built: BuiltStyle): void {
  built.sources[sourceId(ref.object)] = { type: 'geojson', data: { type: 'FeatureCollection', features: [] } };
}

/**
 * Crowns come as tiles from the layer's worker: one point per tree below CROWN_POLYGON_MIN_ZOOM,
 * where a crown is a pixel anyway, and the outlines from there up.
 */
/** A line as the map draws it and the legend shows it. */
export interface LineLook {
  color: string;
  width: number;
  dash?: number[];
}

/** A point marker filled in the genus colour. */
export const GENUS_FILL = 'genus';

/** A point marker: filled in `fill` (`GENUS_FILL`: in the genus colour; null: hollow), ringed by `stroke`. */
export interface PointLook {
  fill: string | null;
  stroke: string;
  strokeWidth: number;
}

/** The outlines the map draws, one look per meaning; the legend reads the same. */
export const LINE_LOOKS = {
  lhp: { color: MAP.lhpLine, width: 2 },
  boundary: { color: MAP.boundaryLine, width: 2 },
  /** A mobile scan's areas (a `lidar_mls` object's coverage). */
  scanArea: { color: MAP.scanArea, width: 2 },
  /** Where the mobile scan reached, over a growing stock's units. */
  mlsArea: { color: MAP.scanArea, width: 2, dash: [3, 2] },
  /** What the field crew surveyed. */
  surveyArea: { color: MAP.surveyArea, width: 2, dash: [1, 2] },
  /** The outline of an MLS crown that is no tree. */
  mlsInvalidCrown: { color: MAP.mlsInvalid, width: 1.2, dash: [2, 2] },
  /** A picked unit, whatever its level. */
  unitSelected: { color: MAP.unitSelected, width: 4 },
  /** A hotspot of dead, snag and declining crowns (a crown health screen). */
  hotspot: { color: CROWN_HEALTH.hotspot, width: 2.5, dash: [3, 1.5] },
} as const satisfies Record<string, LineLook>;

/** The kinds of unit a growing stock lays out (its `level`), coarsest first, each with its outline. */
export const UNIT_LEVEL_LOOKS = {
  area: { color: MAP.unit, width: 3 },
  lhc: { color: MAP.unit, width: 2.5 },
  odd: { color: MAP.unit, width: 2 },
  dil: { color: MAP.unit, width: 1.75 },
  por: { color: MAP.unit, width: 1.5 },
  psk: { color: MAP.unit, width: 1.25 },
  mls: { color: MAP.scanArea, width: 2 },
  field: { color: MAP.surveyArea, width: 2 },
} as const satisfies Record<string, LineLook>;
export type UnitLevel = keyof typeof UNIT_LEVEL_LOOKS;
export const UNIT_LEVELS = Object.keys(UNIT_LEVEL_LOOKS) as UnitLevel[];
/** A unit without a level of the list. */
export const UNIT_LOOK: LineLook = { color: MAP.unit, width: 1.5 };
/** The property naming a unit's level in `units.gpkg`. */
export const UNIT_LEVEL = 'level';

/** The markers of the trunks and stems: a living tree in its genus colour, a snag ringed dark, a stump hollow. */
export const POINT_LOOKS = {
  tree: { fill: GENUS_FILL, stroke: MAP.treeOutline, strokeWidth: 0.5 },
  fieldTrunk: { fill: GENUS_FILL, stroke: MAP.fieldTrunkOutline, strokeWidth: 0.5 },
  fieldSnag: { fill: GENUS_FILL, stroke: MAP.fieldSnagRing, strokeWidth: 2 },
  fieldStump: { fill: null, stroke: MAP.fieldStumpRing, strokeWidth: 2 },
  /** An MLS segment that is no tree. */
  mlsInvalid: { fill: null, stroke: MAP.mlsInvalid, strokeWidth: 1.5 },
} as const satisfies Record<string, PointLook>;

const lineCase = (look: LineLook) => ({ 'line-color': look.color, 'line-width': look.width, ...(look.dash ? { 'line-dasharray': look.dash } : {}) });

/** The feature state `MapView` sets on the crown whose popup is open. */
export const SELECTED_STATE = 'selected';
const selected = ['boolean', ['feature-state', SELECTED_STATE], false];

/** Marks a layer of areas: a tree inside one, or on its outline, takes the click from it. */
const AREA = { metadata: { area: true } };

/** The shared vector source of an archive's layers; added once however many layers draw from it. */
function tiledSource(tiles: TiledSource, built: BuiltStyle): string {
  const id = sourceId(tiles.source);
  built.sources[id] ??= { type: 'vector', tiles: [tiles.url], minzoom: 0, maxzoom: tiles.maxzoom, ...(tiles.promoteId ? { promoteId: tiles.promoteId } : {}) };
  return id;
}

function crownsOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  // From the worker's tiles, or from an archive that already holds them (same layers, same look).
  const source = ref.tiles ? tiledSource(ref.tiles, built) : sourceId(ref.object);
  const names = ref.tiles?.layers ?? CROWN_LAYERS;
  if (!ref.tiles) built.sources[source] = { type: 'vector', tiles: [crownTileUrl(ref.object, ref.digest, options.crownVersion)], minzoom: 0, maxzoom: CROWN_TILE_MAX_ZOOM };
  const opacity = opacityOf(options, ref.object, ref.object_kind);
  const layout = { visibility: visibilityOf(options, ref.object, ref.object_kind) };
  // MapLibre validates `filter` by key, not by value, so an unfiltered layer carries none.
  const filter = treeFilter(options.filters, options);
  const common = { source, ...(filter ? { filter } : {}), layout };
  built.layers.push({
    id: layerId(ref.object, 'points'),
    type: 'circle',
    ...common,
    'source-layer': names.points,
    maxzoom: CROWN_POLYGON_MIN_ZOOM,
    paint: {
      'circle-color': treeColor(options, 'photo'),
      // The selected crown keeps its colour and grows. A zoom curve must be the outermost expression.
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, ['case', selected, 5, 1], CROWN_POLYGON_MIN_ZOOM, ['case', selected, 7, 3]],
      'circle-opacity': opacity,
    },
  });
  crownPolygonsOf(ref, options, built, { common: { ...common, 'source-layer': names.polygons, minzoom: CROWN_POLYGON_MIN_ZOOM }, color: treeColor(options, 'photo') });
}

/**
 * A crown layer's polygons, wherever they come from: filled (or outlined, per the panel) in
 * `color`, the selected crown in its own colour with a heavy line, mature ones highlighted.
 * Photo crowns and an MLS analysis's crowns share it, so a genus looks the same in both.
 */
function crownPolygonsOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle, { common, color }: { common: Partial<LayerSpec> & { source: string }; color: unknown }): void {
  const opacity = opacityOf(options, ref.object, ref.object_kind);
  const outline = options.crownStyle === 'outline';
  const mature = matureExpression(options);
  built.layers.push({
    id: layerId(ref.object, 'fill'),
    type: 'fill',
    ...common,
    // An outlined crown keeps its fill, invisible, so a click inside it still opens the popup.
    // The selected crown keeps its colour, filled more strongly.
    paint: { 'fill-color': color, 'fill-opacity': ['case', selected, (outline ? 0.4 : 0.9) * opacity, outline ? 0 : 0.6 * opacity] },
  });
  const lineColor = outline ? color : MAP.treeOutline;
  const lineWidth = outline ? 1.5 : 0.5;
  built.layers.push({
    id: layerId(ref.object, 'line'),
    type: 'line',
    ...common,
    paint: {
      'line-color': ['case', selected, color, ...(options.highlightMature ? [mature, MATURITY.highlight] : []), lineColor],
      'line-width': ['case', selected, 4, ...(options.highlightMature ? [mature, outline ? 3 : 2.5] : []), lineWidth],
      'line-opacity': opacity,
    },
  });
}

function linesOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  const look = ref.object_kind === 'lhp' ? LINE_LOOKS.lhp : LINE_LOOKS.boundary;
  built.layers.push({
    id: layerId(ref.object, 'line'),
    type: 'line',
    source: sourceId(ref.object),
    ...AREA,
    layout: { visibility: visibilityOf(options, ref.object, ref.object_kind) },
    paint: { ...lineCase(look), 'line-opacity': opacityOf(options, ref.object, ref.object_kind) },
  });
}

/** The `area` table of a scan's `coverage.gpkg` (forun's MLS preprocess). */
export const SCAN_AREA_ID = 'area_id';

/** A scan's areas: outlined, lightly filled so a click inside selects, numbered, the selected one stronger. */
function scanAreasOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  const opacity = opacityOf(options, ref.object, ref.object_kind);
  const layout = { visibility: visibilityOf(options, ref.object, ref.object_kind) };
  const source = sourceId(ref.object);
  const selected = ['==', ['get', SCAN_AREA_ID], options.selectedArea ?? -1];
  built.layers.push({
    id: layerId(ref.object, 'fill'),
    type: 'fill',
    source,
    ...AREA,
    layout,
    paint: { 'fill-color': MAP.scanAreaFill, 'fill-opacity': ['case', selected, 1, 0.6 * opacity] },
  });
  built.layers.push({
    id: layerId(ref.object, 'line'),
    type: 'line',
    source,
    ...AREA,
    layout,
    paint: {
      'line-color': ['case', selected, MAP.scanAreaSelected, LINE_LOOKS.scanArea.color],
      'line-width': ['case', selected, 4, LINE_LOOKS.scanArea.width],
      'line-opacity': opacity,
    },
  });
  built.layers.push({
    id: layerId(ref.object, 'label'),
    type: 'symbol',
    source,
    layout: { ...layout, 'text-field': ['to-string', ['get', SCAN_AREA_ID]], 'text-font': LABEL_FONT, 'text-size': 14 },
    paint: { 'text-color': MAP.scanAreaSelected, 'text-halo-color': MAP.scanAreaLabelHalo, 'text-halo-width': 1.5, 'text-opacity': opacity },
  });
}

/** The genus an MLS segment carries when it is not a tree. */
export const MLS_INVALID = 'invalid';

/** The colour of an MLS segment: a tree as every tree layer is coloured, a segment that is no tree grey. */
function mlsColor(options: LayerOptions): unknown[] {
  return ['case', ['==', ['get', TREE_FIELDS.genus], MLS_INVALID], MAP.mlsInvalid, treeColor(options, 'mls')];
}

/** Where an MLS layer is drawn from: its archive's shared source and source layer, or its own GeoJSON. */
function mlsSource(ref: LayerRef, built: BuiltStyle, part: 'points' | 'polygons'): { source: string; 'source-layer'?: string } {
  if (!ref.tiles) return { source: sourceId(ref.object) };
  return { source: tiledSource(ref.tiles, built), 'source-layer': ref.tiles.layers[part] };
}

/** An MLS analysis's stems (its `tree` table): coloured as the crowns are, a segment that is no tree hollow; the selected one grows. */
function mlsStemsOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  const opacity = opacityOf(options, ref.object, ref.object_kind);
  const invalid = ['==', ['get', TREE_FIELDS.genus], MLS_INVALID];
  const filter = mlsFilter(options.filters);
  built.layers.push({
    id: layerId(ref.object, 'points'),
    type: 'circle',
    ...mlsSource(ref, built, 'points'),
    ...(filter ? { filter } : {}),
    layout: { visibility: visibilityOf(options, ref.object, ref.object_kind) },
    paint: {
      'circle-color': mlsColor(options),
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 14, ['case', selected, 5, 2], 20, ['case', selected, 10, 6]],
      'circle-opacity': ['case', invalid, 0, opacity],
      'circle-stroke-width': ['case', selected, 2.5, invalid, POINT_LOOKS.mlsInvalid.strokeWidth, POINT_LOOKS.tree.strokeWidth],
      'circle-stroke-color': ['case', selected, MAP.linkLine, invalid, POINT_LOOKS.mlsInvalid.stroke, POINT_LOOKS.tree.stroke],
      'circle-stroke-opacity': opacity,
    },
  });
}

/**
 * An MLS analysis's crowns (its `crown` table), drawn by the photo crowns' own builder; a
 * segment that is no tree is grey, with a dashed outline of its own.
 */
function mlsCrownsOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  const opacity = opacityOf(options, ref.object, ref.object_kind);
  const layout = { visibility: visibilityOf(options, ref.object, ref.object_kind) };
  const source = mlsSource(ref, built, 'polygons');
  const invalid = ['==', ['get', TREE_FIELDS.genus], MLS_INVALID];
  const filter = mlsFilter(options.filters);
  // As with the photo crowns, outlines only from the zoom where a crown is more than a pixel; the stems stand for them below.
  crownPolygonsOf(ref, options, built, { common: { ...source, minzoom: CROWN_POLYGON_MIN_ZOOM, ...(filter ? { filter } : {}), layout }, color: mlsColor(options) });
  built.layers.push({
    id: layerId(ref.object, 'dash'),
    type: 'line',
    ...source,
    minzoom: CROWN_POLYGON_MIN_ZOOM,
    // A genus picked hides the segments that are no tree, their dashes too.
    filter: options.filters?.species?.length ? ['all', invalid, false] : invalid,
    layout,
    paint: { ...lineCase(LINE_LOOKS.mlsInvalidCrown), 'line-opacity': opacity },
  });
}

/**
 * A crown health screen's hotspots (the `hotspots` layer of its archive): outlined, filled just
 * enough that a click inside opens its popup; the selected one heavier.
 */
function hotspotsOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  if (!ref.tiles?.areas) return;
  const opacity = opacityOf(options, ref.object, ref.object_kind);
  const common = { source: tiledSource(ref.tiles, built), 'source-layer': ref.tiles.areas, ...AREA, layout: { visibility: visibilityOf(options, ref.object, ref.object_kind) } };
  built.layers.push({ id: layerId(ref.object, 'fill'), type: 'fill', ...common, paint: { 'fill-color': CROWN_HEALTH.hotspot, 'fill-opacity': ['case', selected, 0.15 * opacity, 0.03 * opacity] } });
  built.layers.push({
    id: layerId(ref.object, 'line'),
    type: 'line',
    ...common,
    paint: { ...lineCase(LINE_LOOKS.hotspot), 'line-width': ['case', selected, 4, LINE_LOOKS.hotspot.width], 'line-opacity': opacity },
  });
}

/** Where the mobile scan reached (the `mls_area` table of a growing stock's `units.gpkg`). */
function mlsAreaOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  built.layers.push({
    id: layerId(ref.object, 'line'),
    type: 'line',
    source: sourceId(ref.object),
    ...AREA,
    layout: { visibility: visibilityOf(options, ref.object, ref.object_kind) },
    paint: { ...lineCase(LINE_LOOKS.mlsArea), 'line-opacity': opacityOf(options, ref.object, ref.object_kind) },
  });
}

/** The areas the field crew surveyed: a forest summary's `field_area`, or the `area` table of field annotations. */
function fieldAreaOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  built.layers.push({
    id: layerId(ref.object, 'line'),
    type: 'line',
    source: sourceId(ref.object),
    ...AREA,
    layout: { visibility: visibilityOf(options, ref.object, ref.object_kind) },
    paint: { ...lineCase(LINE_LOOKS.surveyArea), 'line-opacity': opacityOf(options, ref.object, ref.object_kind) },
  });
}

/** A GeoPackage flag: written as an integer, read as a number or a boolean. */
const flag = (name: string) => ['to-boolean', ['coalesce', ['get', name], false]];

/**
 * A field crew's trunks (the `trunk` table): coloured as the crowns and stems are; a snag
 * ringed dark, a stump hollow, so neither passes for a living tree.
 */
function fieldTrunksOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  const opacity = opacityOf(options, ref.object, ref.object_kind);
  const snag = flag('snag');
  const stump = flag('stump');
  const filter = genusFilter(options.filters?.species);
  built.layers.push({
    id: layerId(ref.object, 'points'),
    type: 'circle',
    source: sourceId(ref.object),
    ...(filter ? { filter } : {}),
    layout: { visibility: visibilityOf(options, ref.object, ref.object_kind) },
    paint: {
      // The fill takes the panel's colouring; the snag's and stump's rings stay over it.
      'circle-color': treeColor(options, 'field'),
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 14, ['case', selected, 5, 2.5], 20, ['case', selected, 10, 7]],
      'circle-opacity': ['case', stump, 0, opacity],
      'circle-stroke-width': ['case', selected, 2.5, stump, POINT_LOOKS.fieldStump.strokeWidth, snag, POINT_LOOKS.fieldSnag.strokeWidth, POINT_LOOKS.fieldTrunk.strokeWidth],
      'circle-stroke-color': ['case', selected, MAP.linkLine, stump, POINT_LOOKS.fieldStump.stroke, snag, POINT_LOOKS.fieldSnag.stroke, POINT_LOOKS.fieldTrunk.stroke],
      'circle-stroke-opacity': opacity,
    },
  });
}

/** A growing stock's units (the `units` layer of its `units.gpkg`) and the column naming each one. */
export const UNIT_ID = 'id';

/** How a report draws its units in parts (`LayerRef.table`): the click target, the outlines and names, the veil over the unpicked. */
export const UNIT_PARTS = { hit: 'hit', outline: 'outline', dim: 'dim' } as const;
/** How strongly the veil hides what lies under the units that are not picked. */
export const UNIT_DIM_OPACITY = 0.3;

/** A unit's outline colour or width by its level. */
function unitLevelMatch(key: 'color' | 'width'): unknown[] {
  return ['match', ['get', UNIT_LEVEL], ...UNIT_LEVELS.flatMap((l) => [l, UNIT_LEVEL_LOOKS[l][key]]), UNIT_LOOK[key]];
}

/** Units: outlined, filled just enough that a click inside selects, named, the selected ones stronger. */
function unitsOf(ref: LayerRef, options: LayerOptions, built: BuiltStyle): void {
  const opacity = opacityOf(options, ref.object, ref.object_kind);
  const layout = { visibility: visibilityOf(options, ref.object, ref.object_kind) };
  const source = sourceId(ref.object);
  const selected = ['in', ['get', UNIT_ID], ['literal', options.selectedUnits ?? []]];
  // The units may come in parts, stacked apart: what a click hits (below the crowns), their
  // outlines and names (above them), and the veil over the unpicked ones (between the two).
  if (ref.table === UNIT_PARTS.dim) {
    built.layers.push({ id: layerId(ref.object, 'dim'), type: 'fill', source, layout, paint: { 'fill-color': MAP.unitDim, 'fill-opacity': UNIT_DIM_OPACITY * opacity } });
    return;
  }
  if (ref.table !== UNIT_PARTS.outline) {
    built.layers.push({
      id: layerId(ref.object, 'fill'),
      type: 'fill',
      source,
      ...AREA,
      layout,
      paint: { 'fill-color': MAP.unitFill, 'fill-opacity': ['case', selected, 0, opacity] },
    });
  }
  if (ref.table === UNIT_PARTS.hit) return;
  built.layers.push({
    // Outlines drawn over the crowns are no click target: the units' fill below takes the clicks.
    id: layerId(ref.object, ref.table === UNIT_PARTS.outline ? 'edge' : 'line'),
    type: 'line',
    source,
    ...AREA,
    layout,
    paint: {
      'line-color': ['case', selected, LINE_LOOKS.unitSelected.color, unitLevelMatch('color')],
      'line-width': ['case', selected, LINE_LOOKS.unitSelected.width, unitLevelMatch('width')],
      'line-opacity': opacity,
    },
  });
  built.layers.push({
    id: layerId(ref.object, 'label'),
    type: 'symbol',
    source,
    layout: { ...layout, 'text-field': ['to-string', ['get', 'name']], 'text-font': LABEL_FONT, 'text-size': 12 },
    paint: { 'text-color': MAP.unitSelected, 'text-halo-color': MAP.unitLabelHalo, 'text-halo-width': 1.5, 'text-opacity': opacity },
  });
}

function photosOf(ref: PhotoObjectRef, options: LayerOptions, built: BuiltStyle, photoColorBy: PhotoColorBy): void {
  const opacity = opacityOf(options, ref.object, ref.object_kind);
  const visibility = visibilityOf(options, ref.object, ref.object_kind);
  const source = sourceId(ref.object);
  built.sources[source] = {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
    cluster: true,
    clusterMaxZoom: PHOTO_CLUSTER_MAX_ZOOM,
    clusterRadius: PHOTO_CLUSTER_RADIUS,
  };
  built.layers.push({
    id: layerId(ref.object, 'clusters'),
    type: 'circle',
    source,
    filter: ['has', 'point_count'],
    layout: { visibility },
    paint: {
      'circle-color': MAP.cluster,
      'circle-opacity': 0.85 * opacity,
      'circle-radius': ['interpolate', ['linear'], ['get', 'point_count'], 1, 10, 25, 16, 100, 24],
    },
  });
  built.layers.push({
    id: layerId(ref.object, 'cluster-count'),
    type: 'symbol',
    source,
    filter: ['has', 'point_count'],
    layout: { visibility, 'text-field': ['to-string', ['get', 'point_count']], 'text-font': LABEL_FONT, 'text-size': 12, 'text-allow-overlap': true },
    paint: { 'text-color': MAP.clusterText },
  });
  built.layers.push({
    id: layerId(ref.object, 'photos'),
    type: 'circle',
    source,
    filter: ['!', ['has', 'point_count']],
    layout: { visibility },
    paint: {
      'circle-radius': 5,
      'circle-color': photoColor(photoColorBy),
      'circle-opacity': opacity,
      'circle-stroke-width': tiltRing(ref, options, PHOTO_TILT_EXCLUDED_LOOK.strokeWidth, 1),
      'circle-stroke-color': tiltRing(ref, options, PHOTO_TILT_EXCLUDED_LOOK.stroke, MAP.photoOutline),
      'circle-stroke-opacity': opacity,
    },
  });
}

/**
 * Coloured by tilt, a photo above the acquisition's limit gets `excluded`: the ramp alone does not
 * tell 10.0° from 10.1°, and the photogrammetry keeps the one and drops the other (strictly above).
 */
function tiltRing<T>(ref: PhotoObjectRef, options: LayerOptions, excluded: T, kept: T): unknown {
  if (options.photoColorBy !== 'tilt_deg') return kept;
  const tilt = ['get', 'tilt_deg'];
  return ['case', ['all', ['==', ['typeof', tilt], 'number'], ['>', tilt, tiltLimitOf(ref)]], excluded, kept];
}

const ROLES = ['raster', 'points', 'fill', 'line', 'dash', 'edge', 'dim', 'label', 'photos', 'clusters', 'cluster-count'] as const;

/** The object a layer was built for. */
export function objectIdOfLayer(id: string): string {
  return id.replace(/^lyr-/, '').replace(new RegExp(`-(${ROLES.join('|')})$`), '');
}

/**
 * The ids of the layers a click should query for a feature popup, topmost last. Photo points
 * are left out: `PhotoLayer` opens them in its own viewer.
 */
export function interactiveLayerIds(built: BuiltStyle): string[] {
  const roles = ['points', 'fill', 'line'];
  return built.layers.filter((l) => roles.some((r) => l.id.endsWith(`-${r}`))).map((l) => l.id);
}

/**
 * The feature a click (or a hover) at a point stands for, of those `queryRenderedFeatures` found
 * there, topmost first: the topmost that is no area, so a tree inside a hotspot, a unit or a scan's
 * area is picked over it whatever their order on the map; an area only where no tree was hit.
 */
export function pickHit<T extends { layer: { id: string } }>(built: BuiltStyle, hits: T[]): T | undefined {
  const areas = new Set(built.layers.filter((l) => l.metadata?.area).map((l) => l.id));
  return hits.find((h) => !areas.has(h.layer.id)) ?? hits[0];
}

export function buildLayers(refs: LayerRef[], options: LayerOptions, photos: PhotoObjectRef[] = []): BuiltStyle {
  const built: BuiltStyle = { sources: {}, layers: [] };

  if (options.basemap !== 'none') {
    const basemap = BASEMAPS[options.basemap ?? 'osm'];
    built.sources[BASEMAP_SOURCE] = {
      type: 'raster',
      tiles: [basemap.url],
      tileSize: 256,
      maxzoom: 19,
      attribution: basemap.attribution,
    };
    built.layers.push({ id: BASEMAP_LAYER, type: 'raster', source: BASEMAP_SOURCE, paint: { 'raster-opacity': 1 } });
  }

  // The panel lists the top layer first and MapLibre draws later layers on top.
  for (const ref of [...orderRefs(refs, options.layers)].reverse()) {
    if (ref.via === 'tiler') rasterOf(ref, options, built);
    else if (ref.via === 'link') {
      if (isCrownKind(ref.object_kind)) crownsOf(ref, options, built);
      else if (ref.object_kind === 'crown_health') {
        // Drawn from its tile archive only: its GeoPackage holds every index of every crown.
        if (ref.table === HEALTH_HOTSPOTS) hotspotsOf(ref, options, built);
        else if (ref.tiles) crownsOf(ref, options, built);
      }
      else if (ref.object_kind === 'lidar_mls') {
        vectorSource(ref, built);
        scanAreasOf(ref, options, built);
      } else if (ref.object_kind === 'forest_summary') {
        vectorSource(ref, built);
        if (ref.table === 'mls_area') mlsAreaOf(ref, options, built);
        else if (ref.table === 'field_area') fieldAreaOf(ref, options, built);
        else unitsOf(ref, options, built);
      } else if (ref.object_kind === 'field_annotations') {
        // A line layer alone would leave the trunks invisible: points are drawn as circles.
        vectorSource(ref, built);
        if (ref.table === FIELD_TABLES.area) fieldAreaOf(ref, options, built);
        else fieldTrunksOf(ref, options, built);
      } else if (ref.object_kind === 'mls_tree_analysis') {
        if (!ref.tiles) vectorSource(ref, built);
        if (ref.table === 'crown') mlsCrownsOf(ref, options, built);
        else mlsStemsOf(ref, options, built);
      } else {
        vectorSource(ref, built);
        linesOf(ref, options, built);
      }
    }
  }
  // Acquisitions are appended after the other layers, on top, which is what the report needs.
  const photoColorBy = photoColorByOf(options, photos);
  for (const ref of photos) photosOf(ref, options, built, photoColorBy);
  return built;
}
